import { forwardRef, useId, type InputHTMLAttributes } from 'react';

import { cx } from '../../lib/cx';
import styles from './textField.module.css';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /**
   * Спрятать подпись ВИЗУАЛЬНО, оставив её скринридеру.
   *
   * Не `label=""`: поле без имени безымянно и для скринридера тоже. Нужно там, где
   * подпись очевидна из содержимого (список точек над списком точек) и стоит строки
   * текста, ничего не добавляя.
   */
  hideLabel?: boolean;
  hint?: string;
  /** Текст ошибки. Присутствие строки само по себе переводит поле в невалидное состояние. */
  error?: string;
  /** Показать код цвета рядом с полем — для `type="color"`. */
  showColorCode?: boolean;
}

/**
 * Поле ввода с меткой.
 *
 * Метка связана с полем через `useId`, а не через обёртывание в `<label>`:
 * обёртка ломает клик по вспомогательным элементам рядом (образец цвета),
 * а связь по id работает одинаково во всех случаях.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hideLabel, hint, error, showColorCode, className, required, value, type, ...rest },
  ref,
) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  // Описание поля — подсказка ИЛИ ошибка, не обе сразу: скринридер зачитал бы их
  // подряд, и пользователь услышал бы объяснение до причины отказа.
  let describedBy: string | undefined;
  if (error) {
    describedBy = errorId;
  } else if (hint) {
    describedBy = hintId;
  }

  const input = (
    <input
      ref={ref}
      id={id}
      type={type}
      value={value}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      className={cx(
        styles.input,
        error ? styles.invalid : undefined,
        showColorCode ? styles.colorSwatch : undefined,
      )}
      {...rest}
    />
  );

  return (
    <div className={cx(styles.field, className)}>
      {/* Звёздочка стоит РЯДОМ с label, а не внутри него: внутри она попадает
          в текст метки, и метка перестаёт совпадать с видимым названием поля —
          для скринридера, для автотестов и для любого поиска по названию. */}
      <span className={cx(styles.labelRow, hideLabel ? styles.visuallyHidden : undefined)}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {required ? (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        ) : null}
      </span>

      {showColorCode ? (
        <span className={styles.colorRow}>
          {input}
          <span className={styles.colorCode}>{String(value ?? '')}</span>
        </span>
      ) : (
        input
      )}

      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
      {!error && hint ? (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
});
